import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface PinnedSegment {
  id: string | number;
  content?: string;
  segment_type: string;
  parent_id: string | number;
}

interface BookmarkedSegment {
  segment_id: string | number;
  question_id: string | number;
  topic_id: string | number;
  topic_idx: number;
  question_number: number;
}

interface SegmentHistoryItem {
  id?: string;
  child_segment_attributes?: Record<string, any>;
  [key: string]: any;
}

interface SegmentState {
  pinnedSegment: PinnedSegment | null;
  pinnedResourceSheet: boolean;
  bookmarkedSegments: BookmarkedSegment[];
  activeSegmentId: string | number | null;
  focusedSegmentId: string | number | null;
  focusedSegmentData: any;
  googleDriveAccessToken: string | null;
  activeMediaId: string | number | null;
  currentHistoryOfSegment: any;
  segmentDuplicateLoading: boolean;
  rearrangeMode: boolean;
  selectedTextForTTS: string | null;
  // Pin actions
  isPinned: (segmentId: string | number) => boolean;
  pinSegment: (segment: PinnedSegment | null) => void;
  unpinSegment: (segmentId: string | number) => void;
  pinResourceSheet: () => void;
  unpinResourceSheet: () => void;

  // Bookmark actions
  isBookmarked: (segmentId: string | number) => boolean;
  bookmarkSegment: (segment: BookmarkedSegment) => void;
  unbookmarkSegment: (segmentId: string | number) => void;

  // Active segment actions
  setActiveSegment: (segmentId: string | number | null) => void;
  setFocusedSegment: (segmentId: string | number | null, segmentData?: any) => void;
  setGoogleDriveAccessToken: (accessToken: string | null) => void;

  // Active media actions
  setActiveMediaId: (mediaId: string | number | null) => void;

  // Selected text for TTS actions
  setTextForTTS: (text: string | null) => void;

  // History actions
  setSegmentHistory: (history: SegmentHistoryItem) => void;

  // Duplicate loading actions
  setSegmentDuplicateLoading: (loading: boolean) => void;

  // Rearrange mode actions
  setRearrangeMode: (rearrangeMode: boolean) => void;
}

export const useSegmentStore = create<SegmentState>()(
  devtools((set, get) => ({
    pinnedSegment: null,
    pinnedResourceSheet: false,
    bookmarkedSegments: [],
    activeSegmentId: null,
    focusedSegmentId: null,
    focusedSegmentData: null,
    googleDriveAccessToken: null,
    activeMediaId: null,
    currentHistoryOfSegment: {},
    segmentDuplicateLoading: false,
    rearrangeMode: false,
    selectedTextForTTS: null,
    isPinned: (segmentId) => {
      const pinnedSegment = get().pinnedSegment;
      return pinnedSegment ? pinnedSegment.id === segmentId : false;
    },

    pinSegment: (segment) => {
      const isPinned = get().isPinned(segment?.id ?? 0);
      if (!isPinned) {
        set(() =>
          segment
            ? {
                pinnedSegment: segment,
                pinnedResourceSheet: false,
              }
            : {
                pinnedSegment: segment,
              }
        );
      }
    },

    unpinSegment: (segmentId) => {
      set((state) => ({
        pinnedSegment: state.pinnedSegment?.id === segmentId ? null : state.pinnedSegment,
      }));
    },

    pinResourceSheet: () => {
      set(() => ({
        pinnedResourceSheet: true,
        pinnedSegment: null,
      }));
    },

    unpinResourceSheet: () => {
      set(() => ({
        pinnedResourceSheet: false,
      }));
    },

    isBookmarked: (segmentId) => {
      return get().bookmarkedSegments.some((segment) => segment.segment_id === segmentId);
    },

    bookmarkSegment: (segment) => {
      const isBookmarked = get().isBookmarked(segment.segment_id);
      if (!isBookmarked) {
        set((state) => ({
          bookmarkedSegments: [...state.bookmarkedSegments, segment],
        }));
      }
    },

    unbookmarkSegment: (segmentId) => {
      set((state) => ({
        bookmarkedSegments: state.bookmarkedSegments.filter(
          (segment) => segment.segment_id !== segmentId
        ),
      }));
    },

    setActiveSegment: (segmentId) => {
      set({ activeSegmentId: segmentId });
    },
    setFocusedSegment: (segmentId, segmentData) => {
      set({ focusedSegmentId: segmentId, focusedSegmentData: segmentData ?? null });
    },
    setGoogleDriveAccessToken: (accessToken) => {
      set({ googleDriveAccessToken: accessToken });
    },

    setActiveMediaId: (mediaId) => {
      set({ activeMediaId: mediaId });
    },

    // Selected text for TTS actions
    setTextForTTS: (text) => {
      set({
        selectedTextForTTS: text,
      });
    },

    // History actions
    setSegmentHistory: (history) => {
      set({
        currentHistoryOfSegment: history,
      });
    },

    // Rearrange mode actions
    setRearrangeMode: (rearrangeMode) => {
      set({ rearrangeMode: rearrangeMode });
    },

    // Duplicate loading actions
    setSegmentDuplicateLoading: (loading) => {
      set({ segmentDuplicateLoading: loading });
    },
  }))
);

export const selectCurrentHistoryOfSegment = (state: SegmentState): any =>
  state.currentHistoryOfSegment;

export const selectActiveMediaId = (state: SegmentState): any => state.activeMediaId;

export const selectIsPinnedSegment = (state: SegmentState): any => state.pinnedSegment !== null;

export const selectPinnedSegment = (state: SegmentState): PinnedSegment | null =>
  state.pinnedSegment;

export const selectPinnedResourceSheet = (state: SegmentState): boolean =>
  state.pinnedResourceSheet;

export const selectRearrangeMode = (state: SegmentState): boolean => state.rearrangeMode;

export const selectFocusedSegmentId = (state: SegmentState) => state.focusedSegmentId;
export const selectFocusedSegmentData = (state: SegmentState) => state.focusedSegmentData;

export const selectTextForTTS = (state: SegmentState): string | null => state.selectedTextForTTS;
